import React from "react";

export default function QuestionnaireCompletionIndicator({ value, trend }) {

    /* =========================
       Safe values
       ========================= */

    const completionPercent =
        typeof value === "number" ? Math.round(value) : 0;

    const trendValues =
        Array.isArray(trend)
            ? trend.map(t => t.value).filter(v => typeof v === "number")
            : [];

    const last = trendValues.length > 0
        ? trendValues[trendValues.length - 1]
        : completionPercent;

    const prev = trendValues.length > 1
        ? trendValues[trendValues.length - 2]
        : last;

    const delta = Math.round((last - prev) * 10) / 10;

    const displayTrend =
        trendValues.length > 0
            ? trendValues.slice(-6)
            : [completionPercent];

    /* =========================
       Doughnut maths
       ========================= */

    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    const progress = (completionPercent / 100) * circumference;

    /* =========================
       Styles (unchanged)
       ========================= */

    const styles = {

        card: {
            display: "flex",
            flexDirection: "column",
            padding: "14px 16px",
            height: "100%"
        },

        body: {
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginTop: "-10px"
        },

        doughnutWrapper: {
            width: "120px",
            height: "120px",
            transform: "rotate(-90deg)"
        },

        doughnutBg: {
            fill: "none",
            stroke: "#eee",
            strokeWidth: 10
        },

        doughnutProgress: {
            fill: "none",
            stroke: "#4c9aff",
            strokeWidth: 10,
            strokeLinecap: "round",
            transition: "stroke-dashoffset 0.6s ease"
        },

        doughnutText: {
            fontSize: "18px",
            fontWeight: 700,
            fill: "#333",
            dominantBaseline: "middle",
            textAnchor: "middle"
        },

        metrics: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
        },

        deltaPositive: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#2e7d32"
        },

        deltaNegative: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#c62828"
        },

        deltaLabel: {
            fontSize: "11px",
            color: "#777",
            marginBottom: "6px"
        },

        trend: {
            display: "flex",
            alignItems: "flex-end",
            height: "28px",
            gap: "3px"
        },

        trendBar: {
            width: "5px",
            backgroundColor: "#bbb",
            borderRadius: "2px"
        },

        trendBarLast: {
            backgroundColor: "#4c9aff"
        }
    };

    /* =========================
       Render
       ========================= */

    return (
        <div style={styles.card}>

            <div style={styles.body}>

                {/* Doughnut */}
                <svg width="120" height="120" style={styles.doughnutWrapper}>
                    <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        style={styles.doughnutBg}
                    />
                    <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        style={styles.doughnutProgress}
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference - progress}
                    />
                    <text
                        x="60"
                        y="60"
                        style={styles.doughnutText}
                        transform="rotate(90 60 60)"
                    >
                        {completionPercent}%
                    </text>
                </svg>

                {/* Metrics */}
                <div style={styles.metrics}>

                    <div style={delta >= 0 ? styles.deltaPositive : styles.deltaNegative}>
                        {delta >= 0 ? "+" : ""}{delta}%
                    </div>

                    <div style={styles.deltaLabel}>
                        vs last period
                    </div>

                    {/* Bullet trend */}
                    <div style={styles.trend}>
                        {displayTrend.map(function (v, i) {
                            return (
                                <div
                                    key={i}
                                    title={v + "%"}
                                    style={{
                                        ...styles.trendBar,
                                        height: Math.max(6, Math.min(28, v)),
                                        ...(i === displayTrend.length - 1
                                            ? styles.trendBarLast
                                            : {})
                                    }}
                                />
                            );
                        })}
                    </div>

                </div>

            </div>

        </div>
    );
}
